import axios from 'axios';
import apiCache from './cache';

// Resolve API base URL at runtime with sensible production default
const resolveDefaultApiBase = () => {
  try {
    const host = window?.location?.hostname || '';
    if (host.endsWith('sortafriends.com')) {
      return 'https://api.sortafriends.com';
    }
  } catch (_) {}
  return 'http://localhost:8000';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || resolveDefaultApiBase();

const API_SECRET_HEADER = process.env.REACT_APP_API_SECRET || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (API_SECRET_HEADER) {
    config.headers['X-Api-Secret'] = API_SECRET_HEADER;
  }
  return config;
});

// Centralized HTTP error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.code;
    const message = (error?.message || '').toUpperCase();

    // If login endpoint returns 4xx/5xx, do not redirect to 500 page;
    // let the login component show an inline error. This avoids confusing UX.
    const url = error.config?.url || '';
    const isAuthLogin = url.includes('/v1/auth/login');

    // Check if this is likely a back button navigation scenario
    // If the user just navigated back, be more lenient with errors
    const isBackNavigation = sessionStorage.getItem('last_navigation_time') && 
      (Date.now() - parseInt(sessionStorage.getItem('last_navigation_time'))) < 5000;

    if (status === 401) {
      // Do NOT redirect for login failures; allow component to show error
      if (!isAuthLogin) {
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('logout'));
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (typeof status === 'number' && status >= 500) {
      // Server returned 5xx
      if (!isAuthLogin && !isBackNavigation) {
        // Dispatch custom event to show error overlay instead of redirecting
        window.dispatchEvent(new CustomEvent('serverError', { 
          detail: { 
            type: 'server_error',
            message: 'We ran into a temporary issue. Be back shortly!',
            status: status
          } 
        }));
      }
      return Promise.reject(error);
    }

    // Some 5xx responses without CORS expose show up as network failures to axios
    const isNetworkFailure =
      code === 'ERR_NETWORK' ||
      message.includes('NETWORK ERROR') ||
      message.includes('ERR_FAILED');
    if (!status && isNetworkFailure) {
      if (!isAuthLogin && !isBackNavigation) {
        // Dispatch custom event to show error overlay instead of redirecting
        window.dispatchEvent(new CustomEvent('serverError', { 
          detail: { 
            type: 'network_error',
            message: 'We ran into a temporary issue. Be back shortly!',
            status: null
          } 
        }));
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/v1/auth/login', credentials),
  register: (userData) => api.post('/v1/users/register', userData),
  recovery: (data) => api.post('/v1/auth/recovery', data),
  reset: (data) => api.post('/v1/auth/reset', data),
  verifyEmail: (token) => api.post('/v1/users/verify-email', { token }),
};

export const userAPI = {
  getCurrentUser: () => {
    const cacheKey = 'currentUser';
    const cached = apiCache.get(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }
    return api.get('/v1/users/me').then(response => {
      apiCache.set(cacheKey, response, 60000); // Cache for 1 minute
      return response;
    });
  },
  getMe: () => {
    const cacheKey = 'currentUser';
    const cached = apiCache.get(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }
    return api.get('/v1/users/me').then(response => {
      apiCache.set(cacheKey, response, 60000); // Cache for 1 minute
      return response;
    });
  },
  getUsers: (offset = 0, limit = 33, orderBy = 'updated_at', orderDesc = true) => 
    api.get(`/v1/users?offset=${offset}&limit=${limit}&order_by=${orderBy}&order_desc=${orderDesc}`),
  getUser: (userId) => api.get(`/v1/users/${userId}`),
  updateAvatar: (avatarId) => api.post('/v1/users/me/avatar', { avatar_id: avatarId }).then(response => {
    apiCache.delete('currentUser'); // Clear user cache on update
    return response;
  }),
  updateBio: (bio) => api.put('/v1/users/me/bio', { bio }).then(response => {
    apiCache.delete('currentUser'); // Clear user cache on update
    return response;
  }),
  deactivateAccount: (confirm) => api.post('/v1/users/me/deactivate', { confirm }),
  /**
   * @typedef {Object} SocialIn
   * @property {('telegram'|'signal')} platform
   * @property {string} value
   * @property {('public'|'friends'|'private')} [visibility='friends']
   */
  /**
   * @typedef {Object} SocialOut
   * @property {('telegram'|'signal')} platform
   * @property {string} value
   * @property {string=} link
   * @property {('public'|'friends'|'private')} visibility
   * @property {boolean} is_verified
   */
  /**
   * PUT /v1/users/me/socials
   * @param {SocialIn} payload
   * @returns {Promise<SocialOut>}
   */
  upsertMySocial: async (payload) => {
    try {
      const res = await api.put('/v1/users/me/socials', payload);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (err?.message || 'Request failed');
      throw new Error(msg);
    }
  },
  /**
   * DELETE /v1/users/me/socials/{platform}
   * @param {('telegram'|'signal')} platform
   * @returns {Promise<{message: string}>}
   */
  deleteMySocial: async (platform) => {
    try {
      const res = await api.delete(`/v1/users/me/socials/${platform}`);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (err?.message || 'Request failed');
      throw new Error(msg);
    }
  },
  // Friend endpoints
  sendFriendRequest: (userId) => api.post('/v1/users/friends/requests', { user_id: userId }),
  getFriendRequests: (type = 'incoming') => api.get(`/v1/users/friends/requests?type=${type}`),
  updateFriendRequest: (requestId, status) => api.put(`/v1/users/friends/requests/${requestId}`, { status }),
  getMyFriends: () => api.get('/v1/users/friends/me'),
  unfriendUser: (userId) => api.delete(`/v1/users/friends/${userId}`),
  // Block endpoints
  blockUser: (userId) => api.post('/v1/users/blocks', { user_id: userId }),
  getBlocks: () => api.get('/v1/users/blocks'),
  unblockUser: (userId) => api.delete(`/v1/users/blocks/${userId}`),
  // Notification endpoints
  getNotifications: () => {
    const cacheKey = 'notifications';
    const cached = apiCache.get(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }
    return api.get('/v1/users/notifications').then(response => {
      apiCache.set(cacheKey, response, 30000); // Cache for 30 seconds
      return response;
    });
  },
  getUnreadNotificationsCount: () => {
    const cacheKey = 'unreadNotificationsCount';
    const cached = apiCache.get(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }
    return api.get('/v1/users/notifications/unread-count').then(response => {
      apiCache.set(cacheKey, response, 30000); // Cache for 30 seconds
      return response;
    });
  },
  markNotificationsRead: (notificationIds) => api.post('/v1/users/notifications/mark-read', { notification_ids: notificationIds }).then(response => {
    apiCache.delete('notifications'); // Clear notifications cache on update
    apiCache.delete('unreadNotificationsCount'); // Clear count cache on update
    return response;
  }),
  // Group endpoints
  getMyGroups: () => api.get('/v1/users/groups/me'),
  getGroupsOverview: () => api.get('/v1/users/groups/overview'),
  getGroupRequests: (type = 'incoming') => api.get(`/v1/users/groups/requests?type=${type}`),
  updateGroupRequest: (requestId, status) => api.put(`/v1/users/groups/requests/${requestId}`, { status }),
  // Shared activity endpoint
  getUserSharedActivity: (userId) => api.get(`/v1/users/${userId}/shared`),
};

export const groupAPI = {
  getGroups: () => api.get('/v1/groups'),
  createGroup: (groupData) => api.post('/v1/groups', groupData),
  getGroup: (groupId) => api.get(`/v1/groups/${groupId}`),
  inviteUserToGroup: (groupId, userId) => api.post(`/v1/groups/${groupId}/invite/${userId}`),
  leaveGroup: (groupId) => api.delete(`/v1/groups/${groupId}/leave`),
  joinGroup: (groupId) => api.post(`/v1/groups/${groupId}/join`),
  retireGroup: (groupId) => api.delete(`/v1/groups/${groupId}`),
  // Group block endpoints (owner only)
  getGroupBlocks: (groupId) => api.get(`/v1/groups/${groupId}/blocks`),
  blockUserFromGroup: (groupId, userId) => api.post(`/v1/groups/${groupId}/blocks`, { group_id: groupId, user_id: userId }),
  unblockUserFromGroup: (groupId, userId) => api.delete(`/v1/groups/${groupId}/blocks/${userId}`),
  // Group bots endpoint
  getGroupBots: (groupId) => api.get(`/v1/groups/${groupId}/bots`),
  // Group description endpoint (owner only)
  updateGroupDescription: (groupId, description) => api.post(`/v1/groups/${groupId}/description`, { description }),
};

export const stackAPI = {
  createStack: (payload) => api.post('/v1/stacks', payload),
  getStack: (stackId) => api.get(`/v1/stacks/${stackId}`),
  completeStack: (stackId, positionIds, stackOrderId = null) =>
    api.post(`/v1/stacks/${stackId}/complete`, { 
      position_ids: positionIds,
      ...(stackOrderId && { stack_order_id: stackOrderId })
    }),
  markStackViewed: (stackId) => api.post(`/v1/stacks/${stackId}/viewed`),
  hideStack: (stackId) => api.delete(`/v1/stacks/${stackId}/hide`, { data: {} }),
  hideStackForUsers: (stackId, userIds) => api.delete(`/v1/stacks/${stackId}/hide`, { data: { user_ids: userIds } }),
  deleteStack: (stackId) => api.delete(`/v1/stacks/${stackId}`),
  setSandbox: (stackId, sandbox) => api.post(`/v1/stacks/${stackId}/sandbox`, { sandbox }),
  editStack: (stackId, payload) => api.post(`/v1/stacks/${stackId}/edit`, payload),
  updateDraft: (stackId, payload) => api.post(`/v1/stacks/${stackId}/draft`, payload),
  publishStack: (stackId, payload) => api.post(`/v1/stacks/${stackId}/publish`, payload),
  collaborateStack: (stackId, payload) => api.post(`/v1/stacks/${stackId}/collaborate`, payload),
  createComment: (stackId, description) => api.post(`/v1/stacks/${stackId}/comments`, { description }),
  updateComment: (stackId, commentId, description) => api.put(`/v1/stacks/${stackId}/comments/${commentId}`, { description }),
  deleteComment: (stackId, commentId) => api.delete(`/v1/stacks/${stackId}/comments/${commentId}`),
  sandboxComment: (stackId, commentId, sandbox = true) => api.post(`/v1/stacks/${stackId}/comments/${commentId}/sandbox`, { sandbox }),
};

export const v2StackAPI = {
  getStacks: (offset = 0, limit = 33, filterNew = false, filterFriends = false, filterNoBots = false, orderBy = 'updated_at') => {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      filter_new: filterNew.toString(),
      filter_friends: filterFriends.toString(),
      filter_no_bots: filterNoBots.toString(),
      order_by: orderBy
    });
    return api.get(`/v2/stacks?${params}`);
  },
  getStackBotStacks: (stackId) => api.get(`/v2/stacks/${stackId}/bot_stacks`),
  completeBotStack: (stackId, botStackId) => api.post(`/v2/stacks/${stackId}/bot_stacks/complete`, { bot_stack_id: botStackId })
};

export const twistedStackAPI = {
  getTwistedStacks: (offset = 0, limit = 33) => api.get(`/v1/twisted_stacks?offset=${offset}&limit=${limit}`),
  createTwistedStack: (payload) =>
    api.post('/v1/twisted_stacks', payload, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    }),
  getTwistedStack: (id) => api.get(`/v1/twisted_stacks/${id}`),
  completeTwistedStack: (id, positionIds) => api.post(`/v1/twisted_stacks/${id}/complete`, { position_ids: positionIds }),
  markTwistedStackViewed: (id) => api.post(`/v1/twisted_stacks/${id}/viewed`),
  hideTwistedStack: (id) => api.delete(`/v1/twisted_stacks/${id}/hide`),
  editTwistedStack: (id, payload) => api.post(`/v1/twisted_stacks/${id}/edit`, payload),
  createComment: (twistedStackId, description) => api.post(`/v1/twisted_stacks/${twistedStackId}/comments`, { description }),
  updateComment: (twistedStackId, commentId, description) => api.put(`/v1/twisted_stacks/${twistedStackId}/comments/${commentId}`, { description }),
  deleteComment: (twistedStackId, commentId) => api.delete(`/v1/twisted_stacks/${twistedStackId}/comments/${commentId}`),
  sandboxComment: (twistedStackId, commentId, sandbox = true) => api.post(`/v1/twisted_stacks/${twistedStackId}/comments/${commentId}/sandbox`, { sandbox }),
};

export const botAPI = {
  getBots: (offset = 0, limit = 33, orderBy = 'updated_at', orderDesc = true) => 
    api.get(`/v1/bots?offset=${offset}&limit=${limit}&order_by=${orderBy}&order_desc=${orderDesc}`),
  getBot: (botId) => api.get(`/v1/bots/${botId}`),
  getBotResponses: (botId, limit = 1, offset = 0) => api.get(`/v1/bots/${botId}/responses?limit=${limit}&offset=${offset}`),
  addBotKeyword: (botId, payload) => api.post(`/v1/bots/${botId}/keywords`, payload),
  removeBotKeyword: (botId, payload) => api.delete(`/v1/bots/${botId}/keywords`, { data: payload }),
  addBotThemeTone: (botId, payload) => api.post(`/v1/bots/${botId}/theme_tones`, payload),
  removeBotThemeTone: (botId, payload) => api.delete(`/v1/bots/${botId}/theme_tones`, { data: payload }),
  getBotPositions: (botId, offset = 0, limit = 5, filterNew = true) => 
    api.get(`/v1/bots/${botId}/positions?offset=${offset}&limit=${limit}&filter_new=${filterNew}`),
  upsertBotPositions: (botId, positions) => api.post(`/v1/bots/${botId}/positions`, positions),
  getBotPosition: (botId, metadataId) => api.get(`/v1/bots/${botId}/positions/${metadataId}`),
  upsertBotPosition: (botId, metadataId, updateData) => api.post(`/v1/bots/${botId}/positions/${metadataId}`, updateData),
  getBotOperations: (
    botId,
    {
      filterNoCompleted = false,
      filterNoFailed = false,
      offset = 0,
      limit = 5,
      orderBy = 'updated_at',
      orderDesc = true,
    } = {}
  ) => {
    const params = new URLSearchParams({
      filter_no_completed: filterNoCompleted,
      filter_no_failed: filterNoFailed,
      offset,
      limit,
      order_by: orderBy,
      order_desc: orderDesc,
    });
    return api.get(`/v2/bots/${botId}/operations?${params.toString()}`);
  },
  getBotOperationDetail: (botId, operationId) =>
    api.get(`/v2/bots/${botId}/operations/${operationId}`),
  failBotOperation: (botId, operationId) =>
    api.delete(`/v2/bots/${botId}/operations/${operationId}`),
  createBotOperation: (botId, payload) =>
    api.post(`/v2/bots/${botId}/operations`, payload, {
      headers: { 'Content-Type': 'application/json' },
    }),
};

export default api;
